import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTransactionStore } from '@/store/transactionsStore';
import { useAuthStore } from '@/store/authStore';
import { useAccounts } from '@/store/accountsStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { usePayeesStore } from '@/store/payeesStore';
import { useTagsStore } from '@/store/tagsStore';
import { LocalTransaction } from '../utils/TransactionFormatters';
import type { SortField } from '@/types/base';

export interface TransactionFilters {
  accounts?: string[];
  search?: string;
  types?: string[];
  categories?: string[];
  payees?: string[];
  tags?: string[];
  startDate?: Date;
  endDate?: Date;
  orderField?: string;
  orderDirection?: 'asc' | 'desc';
}

export interface UseTransactionDataProps {
  accountId?: string;
}

export const useTransactionData = ({ accountId }: UseTransactionDataProps) => {
  const { user } = useAuthStore();
  const { accounts } = useAccounts();
  const { categories } = useCategoriesStore();
  const { payees } = usePayeesStore();
  const { tags } = useTagsStore();
  const transactionStore = useTransactionStore();
  
  // Use web transaction store for data instead of core store
  const webTransactions = transactionStore.transactions || [];
  const webIsLoading = transactionStore.isLoading;
  const webFilters = transactionStore.filters;
  const webFiltersInitialized = transactionStore.filtersInitialized;
  
  const [isLoading, setIsLoading] = useState(false);
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isColumnSorting, setIsColumnSorting] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  
  // Use web store loading state and pagination
  const hasMoreData = transactionStore.pagination?.hasMore || false;
  const pageSize = transactionStore.pagination?.pageSize || 200;
  const isPaginationLoading = transactionStore.isLoading;
  
  // Use ref to store current sort state to avoid dependency issues
  const sortStateRef = useRef({ field: sortField, direction: sortDirection });
  // Use ref to track initialization to prevent infinite loops
  const hasInitializedRef = useRef(false);
  // Use ref to track loading state to prevent race conditions
  const isLoadingRef = useRef(false);
  // Use ref to track timeout for loading state
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get filters from web store instead of treating them as separate
  const storeFilters = webFilters;
  const filtersInitialized = webFiltersInitialized;

  // Use web transactions instead of core transactions
  const coreTransactions = useMemo(() => 
    webTransactions || [], 
    [webTransactions]
  );
  // Use web transaction store's pagination total for filtered count
  const totalItems = transactionStore.pagination?.total || 0;
  /**
   * Build a fast lookup map for category display information
   */
  const categoryLookup = useMemo(() => {
    const map = new Map<
      string,
      {
        displayName: string;
        mainCategoryId?: string;
        mainCategoryName?: string;
        subcategoryName?: string;
      }
    >();

    categories.forEach((mainCategory: {
      id?: string;
      name?: string;
      subcategories?: Array<{
        id?: string;
        name?: string;
      }>;
    }) => {
      if (!mainCategory || !mainCategory.id) {
        return;
      }

      map.set(mainCategory.id, {
        displayName: mainCategory.name ?? 'Uncategorized',
        mainCategoryId: mainCategory.id,
        mainCategoryName: mainCategory.name,
      });

      if (Array.isArray(mainCategory.subcategories)) {
        mainCategory.subcategories.forEach((subCategory: {
          id?: string;
          name?: string;
        }) => {
          if (!subCategory || !subCategory.id) {
            return;
          }

          map.set(subCategory.id, {
            displayName: mainCategory.name
              ? `${mainCategory.name} -> ${subCategory.name}`
              : subCategory.name ?? 'Uncategorized',
            mainCategoryId: mainCategory.id,
            mainCategoryName: mainCategory.name,
            subcategoryName: subCategory.name,
          });
        });
      }
    });

    return map;
  }, [categories]);

  /**
   * Sort transactions based on current sort settings
   */
  const sortTransactions = useCallback((transactions: LocalTransaction[]): LocalTransaction[] => {
    return [...transactions].sort((a, b) => {
      let aValue: any = a[sortField as keyof LocalTransaction];
      let bValue: any = b[sortField as keyof LocalTransaction];

      // Handle date sorting
      if (sortField === 'date') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      // Handle amount sorting
      else if (sortField === 'amount') {
        aValue = Math.abs(aValue || 0);
        bValue = Math.abs(bValue || 0);
      }
      
      // Handle string sorting
      else if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Compare values
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortField, sortDirection]);

  /**
   * Get filtered and sorted transactions
   * Optimized to avoid re-sorting when just appending new transactions
   */
  const filteredTransactions = useMemo(() => {
    if (!filtersInitialized || !user) return [];

    // Since the server returns transactions already sorted by date DESC,
    // and we're using server-side filtering, we can skip client-side sorting
    // for most cases to avoid re-processing the entire list on pagination
    const enrichedTransactions: LocalTransaction[] = coreTransactions.map(tx => {
      const account = accounts.find(acc => acc.id === tx.account_id);
      const categoryInfo = tx.category_id ? categoryLookup.get(tx.category_id) : undefined;
      const payee = payees.find(p => p.id === tx.payee_id);

      return {
        ...tx,
        accountId: tx.account_id,
        categoryId: tx.category_id,
        payeeId: tx.payee_id,
        date: new Date(tx.date),
        _categoryDisplayName: categoryInfo?.displayName || 'Uncategorized',
        _accountDisplayName: account?.name || 'Unknown Account',
        payee: payee?.name || tx.payee_name || '',
        currencyId: account?.currency || tx.currency || 'USD'
      };
    });

    // Only apply client-side sorting if the sort field/direction is different from server default
    // Server returns data sorted by 'date' DESC by default
    if (sortField === 'date' && sortDirection === 'desc') {
      // Use server-provided order (already sorted correctly)
      return enrichedTransactions;
    } else {
      // Apply client-side sorting only when needed
      return sortTransactions(enrichedTransactions);
    }
  }, [
    coreTransactions,
    filtersInitialized,
    user?.id,
    accounts,
    categoryLookup,
    payees,
    sortField,
    sortDirection
  ]);

  // Update ref when sort state changes
  sortStateRef.current = { field: sortField, direction: sortDirection };

  /**
   * Handle sorting
   */
  const handleSort = useCallback(async (field: SortField) => {
    setIsColumnSorting(true);
    
    try {
      const currentField = sortStateRef.current.field;
      const currentDirection = sortStateRef.current.direction;
      
      const newDirection = currentField === field && currentDirection === 'desc' ? 'asc' : 'desc';
      
      setSortField(field);
      setSortDirection(newDirection);
      
      // Update ref immediately
      sortStateRef.current = { field, direction: newDirection };

    } catch (error) {
      console.error('Error sorting transactions:', error);
    } finally {
      setIsColumnSorting(false);
    }
  }, []);

  /**
   * Load more transactions (for pagination)
   */
  const loadMore = useCallback(async () => {
    // Enhanced guard conditions to prevent infinite loading
    if (!user?.id || isPaginationLoading || !hasMoreData || isLoadingRef.current) {
      console.log('Skipping loadMore:', { 
        hasUser: !!user?.id, 
        isPaginationLoading, 
        hasMoreData,
        isLoadingRef: isLoadingRef.current
      });
      return;
    }

    // Additional check to prevent rapid successive calls
    const now = Date.now();
    const timeSinceLastCall = now - (loadMore as any).lastCallTime;
    if (timeSinceLastCall < 1000) { // Prevent calls within 1 second
      console.log('Skipping loadMore: Too frequent calls');
      return;
    }
    (loadMore as any).lastCallTime = now;

          console.log('Loading more transactions...');
    
    // Set both ref and state to prevent race conditions
    isLoadingRef.current = true;
    setIsLoading(true);

    // Set a timeout to prevent stuck loading state
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    loadingTimeoutRef.current = setTimeout(() => {
      console.warn('LoadMore timeout reached, resetting loading state');
      isLoadingRef.current = false;
      setIsLoading(false);
    }, 30000); // 30 second timeout

    try {
      // Use fetchNextPage for proper pagination (now uses offset-based pagination)
      // This will append new transactions to the existing list in the store
      await transactionStore.fetchNextPage(user.id, {
        limit: transactionStore.pagination?.pageSize || 200
      });
      
      // Note: We don't need to handle the returned transactions here
      // because fetchNextPage updates the store state directly,
      // and our filteredTransactions will automatically update via the store subscription
      
    } catch (error) {
      console.error('Error loading more transactions:', error);
    } finally {
      // Clear timeout and reset both ref and state
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      isLoadingRef.current = false;
      setIsLoading(false);

    }
  }, [
    user?.id, 
    isPaginationLoading, 
    hasMoreData,
    webTransactions,
    transactionStore.fetchNextPage,
    transactionStore.pagination?.pageSize
  ]);

  /**
   * Fetch transactions with current filters
   */
  const fetchTransactionsWithFilters = useCallback(async (_reset = true) => {
    if (!user?.id || !filtersInitialized) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Use web transaction store instead of core store
      // The web store has proper server-side filtering
      await transactionStore.fetchTransactions(user.id, {
        page: 1,
        pageSize: 200,
        orderBy: 'date',
        orderDirection: 'desc'
      });
      
    } catch (error) {
      console.error('Error fetching transactions with filters:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, filtersInitialized, transactionStore.fetchTransactions]);

  /**
   * Apply accountId prop to store filters when provided
   */
  useEffect(() => {
    if (!filtersInitialized) return;
    
    if (accountId) {
      // If accountId is provided, set it as the account filter
      const currentAccountFilters = storeFilters.accounts || [];
      if (!currentAccountFilters.includes(accountId)) {
        console.log('Setting account filter from accountId prop:', accountId);
        transactionStore.setFilters({
          ...storeFilters,
          accounts: [accountId]
        });
      }
    } else {
      // If no accountId prop, clear account filters to show all accounts
      const currentAccountFilters = storeFilters.accounts || [];
      if (currentAccountFilters.length > 0) {
        console.log('Clearing account filters (no accountId prop)');
        transactionStore.setFilters({
          ...storeFilters,
          accounts: []
        });
      }
    }
  }, [accountId, filtersInitialized, storeFilters.accounts, transactionStore]);

  /**
   * Initial data load - run only once when the component mounts and user/filters are ready
   * SIMPLIFIED: The web transaction store handles this automatically with proper filter persistence
   */
  useEffect(() => {
    if (!user?.id || !filtersInitialized || hasInitializedRef.current) {
      return;
    }

    // Mark as initialized - the web transaction store handles initial data loading
    hasInitializedRef.current = true;
    
    // Only fetch if we don't have any transactions yet
    if (webTransactions.length === 0 && !webIsLoading) {
      fetchTransactionsWithFilters(true);
    }
  }, [user?.id, filtersInitialized, webTransactions.length, webIsLoading, fetchTransactionsWithFilters]);

  /**
   * Reset initialization flag when user changes
   */
  useEffect(() => {
    hasInitializedRef.current = false;
  }, [user?.id]);

  /**
   * Handle accountId prop changes - reload data when accountId changes
   * SIMPLIFIED: The web transaction store handles filter changes automatically
   */
  useEffect(() => {
    if (!user?.id || !filtersInitialized) {
      return;
    }

    // The web transaction store handles accountId filter changes automatically
    // We only need to trigger a refresh if explicitly needed
    if (accountId && webTransactions.length === 0) {
      fetchTransactionsWithFilters(true);
    }
  }, [accountId, user?.id, filtersInitialized, webTransactions.length, fetchTransactionsWithFilters]);

  /**
   * Toggle transaction selection
   */
  const toggleTransactionSelection = useCallback((transactionId: string) => {
    setSelectedTransactions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(transactionId)) {
        newSet.delete(transactionId);
      } else {
        newSet.add(transactionId);
      }
      return newSet;
    });
  }, []);

  /**
   * Toggle all transactions selection
   */
  const toggleAllTransactions = useCallback(() => {
    const allTransactionIds = filteredTransactions.map(tx => tx.id);
    const allSelected = allTransactionIds.every(id => selectedTransactions.has(id));
    
    if (allSelected) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(allTransactionIds));
    }
  }, [filteredTransactions, selectedTransactions]);

  // Calculate combined loading state for components
  const combinedIsLoading = webIsLoading || isLoading;



  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      // Reset loading states
      isLoadingRef.current = false;
    };
  }, []);

  return {
    // Data
    transactions: filteredTransactions,
    totalItems,
    isLoading: combinedIsLoading,
    hasMoreData,
    pageSize,
    
    // Sorting
    sortField,
    sortDirection,
    isColumnSorting,
    handleSort,
    
    // Selection
    selectedTransactions,
    toggleTransactionSelection,
    toggleAllTransactions,
    setSelectedTransactions,
    
    // Actions
    loadMore,
    refreshTransactions: fetchTransactionsWithFilters,
    
    // Store data
    accounts,
    categories,
    payees,
    tags,
    storeFilters,
    filtersInitialized
  };
}; 